import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

// Get current user ID
const getCurrentUserId = () => {
  const auth = getAuth();
  return auth.currentUser?.uid;
};

// Get user's projects collection reference
const getUserProjectsCollection = () => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return collection(db, 'users', userId, 'projects');
};

// Get specific project document reference
const getProjectDoc = (projectId) => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return doc(db, 'users', userId, 'projects', projectId.toString());
};

// Get equipment collection reference for a project
const getEquipmentCollection = (projectId) => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return collection(db, 'users', userId, 'projects', projectId.toString(), 'equipment');
};

// Get conductors collection reference for a project
const getConductorsCollection = (projectId) => {
  const userId = getCurrentUserId();
  if (!userId) {
    throw new Error('User not authenticated');
  }
  return collection(db, 'users', userId, 'projects', projectId.toString(), 'conductors');
};

// Get shared projects collection reference
const getSharedProjectsCollection = () => {
  return collection(db, 'shared_projects');
};

// Validate document size before saving to Firestore
const validateDocumentSize = (data, maxSizeBytes = 1000000) => { // 1MB limit
  const jsonString = JSON.stringify(data);
  const sizeInBytes = new Blob([jsonString]).size;
  
  if (sizeInBytes > maxSizeBytes) {
    const sizeInMB = (sizeInBytes / 1024 / 1024).toFixed(2);
    throw new Error(`Document size (${sizeInMB}MB) exceeds Firestore limit of 1MB. Please reduce photo quality or remove some photos.`);
  }
  
  return sizeInBytes;
};

// Save project to cloud storage only
export const saveProject = async (projectData) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Generate project ID if not provided
    const projectId = projectData.id || Date.now().toString();
    
    // Validate and clean coordinates before saving
    const validateCoordinates = (coords) => {
      if (!Array.isArray(coords)) return null;
      if (coords.length !== 2) return null;
      const [lng, lat] = coords;
      if (typeof lng !== 'number' || typeof lat !== 'number') return null;
      if (isNaN(lng) || isNaN(lat)) return null;
      return [lng, lat];
    };

    // Validate equipment coordinates
    const validatedEquipment = projectData.equipment?.map(item => {
      const validLngLat = validateCoordinates(item.lngLat);
      const validCoordinates = validateCoordinates(item.coordinates);
      
      if (!validLngLat) {
        console.warn('Invalid equipment lngLat:', item.lngLat, 'for equipment:', item.type);
      }
      
      return {
        ...item,
        lngLat: validLngLat || [0, 0], // Fallback to prevent NaN
        coordinates: validCoordinates || validLngLat || [0, 0]
      };
    }) || [];

    // Validate conductor coordinates
    const validatedConductors = projectData.conductors?.map(item => {
      const validCoordinates = item.coordinates?.map(coord => validateCoordinates(coord)).filter(Boolean);
      
      if (!validCoordinates || validCoordinates.length < 2) {
        console.warn('Invalid conductor coordinates:', item.coordinates, 'for conductor:', item.type);
      }
      
      return {
        ...item,
        coordinates: validCoordinates || [[0, 0], [0, 0]] // Fallback to prevent NaN
      };
    }) || [];
    
    // Prepare project data for Firestore - match the exact structure from GridMapProApp
    const cleanProjectData = {
      id: projectId,
      name: projectData.name || 'Untitled Project',
      customer: projectData.customer || 'Unknown Customer',
      customerAddress: projectData.customerAddress || '',
      customerPhone: projectData.customerPhone || '',
      type: projectData.type || 'residential',
      status: projectData.status || 'in-progress',
      userId: userId,
      lastSynced: serverTimestamp(),
      createdAt: projectData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Store equipment and conductors counts
      equipmentCount: validatedEquipment.length,
      conductorsCount: validatedConductors.length,
      // Store map data exactly as saved from GridMapProApp
      mapCenter: validateCoordinates(projectData.mapCenter) || [-122.4194, 37.7749],
      mapZoom: projectData.mapZoom || 13,
      mapStyle: projectData.mapStyle || 'streets',
      // Store viewport data if provided - flatten bounds to avoid nested arrays
      viewport: projectData.viewport ? {
        bounds: projectData.viewport.bounds ? {
          southwest: projectData.viewport.bounds[0] ? {
            lng: projectData.viewport.bounds[0][0],
            lat: projectData.viewport.bounds[0][1]
          } : null,
          northeast: projectData.viewport.bounds[1] ? {
            lng: projectData.viewport.bounds[1][0],
            lat: projectData.viewport.bounds[1][1]
          } : null
        } : null,
        padding: projectData.viewport.padding || null
      } : null
    };
    
    // Use batch write for atomic operations
    const batch = writeBatch(db);
    
    // Save project data
    const projectRef = getProjectDoc(projectId);
    batch.set(projectRef, cleanProjectData);
    
    // Save equipment if provided - match exact structure from GridMapProApp
    if (validatedEquipment.length > 0) {
      const equipmentCollection = getEquipmentCollection(projectId);
      
      // Clear existing equipment
      const existingEquipment = await getDocs(equipmentCollection);
      existingEquipment.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Add new equipment with exact structure from GridMapProApp
      for (let index = 0; index < validatedEquipment.length; index++) {
        const item = validatedEquipment[index];
        const equipmentRef = doc(equipmentCollection, index.toString());
        const equipmentData = {
          id: item.id || index + 1,
          type: item.type,
          lngLat: item.lngLat,
          coordinates: item.coordinates, // Keep both for compatibility
          details: item.details || {},
          notes: item.notes || '',
          photos: item.photos || [],
          properties: item.properties || {
            ...item.details,
            notes: item.notes,
            photos: item.photos || []
          },
          projectId: projectId,
          index: index,
          createdAt: new Date().toISOString()
        };
        
        // Validate document size before adding to batch
        try {
          validateDocumentSize(equipmentData);
          batch.set(equipmentRef, equipmentData);
        } catch (sizeError) {
          console.error(`Equipment ${index + 1} (${item.type}) is too large:`, sizeError.message);
          // Try to save without photos if the document is too large
          const equipmentDataWithoutPhotos = {
            ...equipmentData,
            photos: [],
            properties: {
              ...equipmentData.properties,
              photos: []
            }
          };
          
          try {
            validateDocumentSize(equipmentDataWithoutPhotos);
            batch.set(equipmentRef, equipmentDataWithoutPhotos);
            console.warn(`Saved equipment ${index + 1} without photos due to size limit`);
          } catch (finalSizeError) {
            console.error(`Equipment ${index + 1} still too large even without photos:`, finalSizeError.message);
            throw new Error(`Equipment ${index + 1} (${item.type}) has too much data. Please reduce notes or details.`);
          }
        }
      }
    }
    
    // Save conductors if provided - match exact structure from GridMapProApp
    if (validatedConductors.length > 0) {
      const conductorsCollection = getConductorsCollection(projectId);
      
      // Clear existing conductors
      const existingConductors = await getDocs(conductorsCollection);
      existingConductors.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Add new conductors with exact structure from GridMapProApp
      for (let index = 0; index < validatedConductors.length; index++) {
        const item = validatedConductors[index];
        const conductorRef = doc(conductorsCollection, index.toString());
        
        // Flatten coordinates to avoid nested arrays issue in Firestore
        const flattenedCoordinates = item.coordinates ? 
          item.coordinates.map(coord => ({
            lng: coord[0],
            lat: coord[1]
          })) : [];
        
        const conductorData = {
          id: item.id || index + 1,
          type: item.type,
          coordinates: flattenedCoordinates, // Use flattened coordinates for Firestore
          wireSize: item.wireSize || '',
          length: item.length || 0,
          notes: item.notes || '',
          photos: item.photos || [],
          properties: item.properties || {
            type: item.type,
            wireSize: item.wireSize,
            length: item.length,
            notes: item.notes,
            photos: item.photos || []
          },
          projectId: projectId,
          index: index,
          createdAt: new Date().toISOString()
        };
        
        // Validate document size before adding to batch
        try {
          validateDocumentSize(conductorData);
          batch.set(conductorRef, conductorData);
        } catch (sizeError) {
          console.error(`Conductor ${index + 1} is too large:`, sizeError.message);
          // Try to save without photos if the document is too large
          const conductorDataWithoutPhotos = {
            ...conductorData,
            photos: [],
            properties: {
              ...conductorData.properties,
              photos: []
            }
          };
          
          try {
            validateDocumentSize(conductorDataWithoutPhotos);
            batch.set(conductorRef, conductorDataWithoutPhotos);
            console.warn(`Saved conductor ${index + 1} without photos due to size limit`);
          } catch (finalSizeError) {
            console.error(`Conductor ${index + 1} still too large even without photos:`, finalSizeError.message);
            throw new Error(`Conductor ${index + 1} has too much data. Please reduce notes or details.`);
          }
        }
      }
    }
    
    // Commit all changes atomically
    await batch.commit();
    
    console.log('Project saved to cloud successfully:', projectId, {
      equipment: validatedEquipment.length,
      conductors: validatedConductors.length,
      mapCenter: cleanProjectData.mapCenter,
      mapZoom: cleanProjectData.mapZoom
    });
    return projectId;
    
  } catch (error) {
    console.error('Error saving project to cloud:', error);
    throw error;
  }
};

// Load project from cloud storage
export const loadProject = async (projectId) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Get project data
    const projectDoc = await getDoc(getProjectDoc(projectId));
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const projectData = projectDoc.data();
    
    // Validate coordinates function
    const validateCoordinates = (coords) => {
      if (!Array.isArray(coords)) return null;
      if (coords.length !== 2) return null;
      const [lng, lat] = coords;
      if (typeof lng !== 'number' || typeof lat !== 'number') return null;
      if (isNaN(lng) || isNaN(lat)) return null;
      return [lng, lat];
    };
    
    // Get equipment - match exact structure from GridMapProApp
    const equipmentCollection = getEquipmentCollection(projectId);
    const equipmentSnapshot = await getDocs(equipmentCollection);
    const equipment = equipmentSnapshot.docs
      .map(doc => {
        const data = doc.data();
        
        // Validate coordinates
        const validLngLat = validateCoordinates(data.lngLat);
        const validCoordinates = validateCoordinates(data.coordinates);
        
        if (!validLngLat) {
          console.warn('Invalid equipment lngLat loaded from cloud:', data.lngLat, 'for equipment:', data.type);
        }
        
        // Return equipment in exact format expected by GridMapProApp
        return {
          id: data.id,
          type: data.type,
          lngLat: validLngLat || [0, 0], // Fallback to prevent NaN
          coordinates: validCoordinates || validLngLat || [0, 0], // Keep both for compatibility
          details: data.details || {},
          notes: data.notes || '',
          photos: data.photos || [],
          properties: data.properties || {
            ...data.details,
            notes: data.notes,
            photos: data.photos || []
          }
        };
      })
      .sort((a, b) => a.index - b.index);

    // Get conductors - match exact structure from GridMapProApp
    const conductorsCollection = getConductorsCollection(projectId);
    const conductorsSnapshot = await getDocs(conductorsCollection);
    const conductors = conductorsSnapshot.docs
      .map(doc => {
        const data = doc.data();
        
        // Reconstruct coordinates from flattened format
        let reconstructedCoordinates = [];
        if (data.coordinates && Array.isArray(data.coordinates)) {
          // Check if coordinates are in flattened format (objects with lng/lat) or array format
          if (data.coordinates.length > 0 && typeof data.coordinates[0] === 'object' && 'lng' in data.coordinates[0]) {
            // Flattened format: convert back to array format
            reconstructedCoordinates = data.coordinates.map(coord => [coord.lng, coord.lat]);
          } else {
            // Already in array format (for backward compatibility)
            reconstructedCoordinates = data.coordinates;
          }
        }
        
        // Validate reconstructed coordinates
        const validCoordinates = reconstructedCoordinates
          .map(coord => validateCoordinates(coord))
          .filter(Boolean);
        
        if (!validCoordinates || validCoordinates.length < 2) {
          console.warn('Invalid conductor coordinates loaded from cloud:', data.coordinates, 'for conductor:', data.type);
        }
        
        // Return conductors in exact format expected by GridMapProApp
        return {
          id: data.id,
          type: data.type,
          coordinates: validCoordinates || [[0, 0], [0, 0]], // Keep as array format
          wireSize: data.wireSize || '',
          length: data.length || 0,
          notes: data.notes || '',
          photos: data.photos || [],
          properties: data.properties || {
            type: data.type,
            wireSize: data.wireSize,
            length: data.length,
            notes: data.notes,
            photos: data.photos || []
          }
        };
      })
      .sort((a, b) => a.index - b.index);

    // Validate map center
    const validMapCenter = validateCoordinates(projectData.mapCenter);
    if (!validMapCenter) {
      console.warn('Invalid map center loaded from cloud:', projectData.mapCenter);
    }

    // Combine all data - match exact structure from GridMapProApp
    const fullProject = {
      id: projectData.id,
      name: projectData.name,
      customer: projectData.customer,
      customerAddress: projectData.customerAddress,
      customerPhone: projectData.customerPhone,
      type: projectData.type,
      status: projectData.status,
      createdAt: projectData.createdAt,
      updatedAt: projectData.updatedAt,
      // Map data
      mapCenter: validMapCenter || [-122.4194, 37.7749], // Fallback to prevent NaN
      mapZoom: projectData.mapZoom || 13,
      mapStyle: projectData.mapStyle || 'streets',
      // Viewport data if available - reconstruct bounds from flattened format
      viewport: projectData.viewport ? {
        bounds: projectData.viewport.bounds ? [
          projectData.viewport.bounds.southwest ? [projectData.viewport.bounds.southwest.lng, projectData.viewport.bounds.southwest.lat] : null,
          projectData.viewport.bounds.northeast ? [projectData.viewport.bounds.northeast.lng, projectData.viewport.bounds.northeast.lat] : null
        ].filter(Boolean) : null,
        padding: projectData.viewport.padding || null
      } : null,
      // Equipment and conductors
      equipment: equipment,
      conductors: conductors
    };

    console.log('Project loaded from cloud:', projectId, {
      equipment: equipment.length,
      conductors: conductors.length,
      mapCenter: fullProject.mapCenter,
      mapZoom: fullProject.mapZoom
    });
    return fullProject;
    
  } catch (error) {
    console.error('Error loading project from cloud:', error);
    throw error;
  }
};

// List all projects from cloud storage
export const listProjects = async () => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const projectsQuery = query(
      getUserProjectsCollection(),
      orderBy('updatedAt', 'desc')
    );
    
    const snapshot = await getDocs(projectsQuery);
    const projects = snapshot.docs.map(doc => ({
      ...doc.data(),
      id: parseInt(doc.id)
    }));

    console.log('Projects loaded from cloud:', projects.length);
    return projects;
    
  } catch (error) {
    console.error('Error listing projects from cloud:', error);
    throw error;
  }
};

// Delete project from cloud storage
export const deleteProject = async (projectId) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const batch = writeBatch(db);
    
    // Delete equipment
    const equipmentCollection = getEquipmentCollection(projectId);
    const equipmentSnapshot = await getDocs(equipmentCollection);
    equipmentSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    // Delete conductors
    const conductorsCollection = getConductorsCollection(projectId);
    const conductorsSnapshot = await getDocs(conductorsCollection);
    conductorsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });
    
    // Delete project
    batch.delete(getProjectDoc(projectId));
    
    // Commit all deletions
    await batch.commit();
    
    console.log('Project deleted from cloud successfully:', projectId);
    
  } catch (error) {
    console.error('Error deleting project from cloud:', error);
    throw error;
  }
};

// Real-time listener for project updates
export const subscribeToProjectUpdates = (callback) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('User not authenticated for real-time updates');
      return () => {};
    }

    const projectsQuery = query(
      getUserProjectsCollection(),
      orderBy('updatedAt', 'desc')
    );
    
    return onSnapshot(projectsQuery, (snapshot) => {
      const projects = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: parseInt(doc.id)
      }));
      
      console.log('Real-time project update received:', projects.length, 'projects');
      callback(projects);
    }, (error) => {
      console.error('Error listening to project updates:', error);
    });
  } catch (error) {
    console.error('Error setting up project listener:', error);
    return () => {};
  }
};

// Real-time listener for specific project updates
export const subscribeToProject = (projectId, callback) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('User not authenticated for project updates');
      return () => {};
    }

    return onSnapshot(getProjectDoc(projectId), (doc) => {
      if (doc.exists()) {
        const projectData = doc.data();
        console.log('Real-time project update received for:', projectId);
        callback(projectData);
      } else {
        console.log('Project no longer exists:', projectId);
        callback(null);
      }
    }, (error) => {
      console.error('Error listening to project updates:', error);
    });
  } catch (error) {
    console.error('Error setting up project listener:', error);
    return () => {};
  }
};

// Debug function to check cloud projects
export const debugCloudProjects = async () => {
  try {
    console.log('=== CLOUD-FIRST PROJECTS DEBUG ===');
    
    const projects = await listProjects();
    console.log('Cloud projects:', projects.map(p => ({ id: p.id, name: p.name, updatedAt: p.updatedAt })));
    
    return {
      totalProjects: projects.length,
      projects: projects
    };
  } catch (error) {
    console.error('Error debugging cloud projects:', error);
    throw error;
  }
};

// Share project with another user
export const shareProject = async (projectId, email) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    // Check if project exists and user owns it
    const projectDoc = await getDoc(getProjectDoc(projectId));
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const projectData = projectDoc.data();
    
    if (projectData.userId !== userId) {
      throw new Error('You can only share projects you own');
    }

    // Get the recipient user ID by email
    const auth = getAuth();
    const usersRef = collection(db, 'users');
    const userQuery = query(usersRef, where('email', '==', email));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      throw new Error('User with this email address not found. They must have an account to receive shared projects.');
    }
    
    const recipientUser = userSnapshot.docs[0];
    const recipientUserId = recipientUser.id;

    // Create a unique ID for the shared project entry
    const sharedProjectId = `${projectId}_${recipientUserId}`;
    const sharedProjectRef = doc(getSharedProjectsCollection(), sharedProjectId);

    // Check if already shared
    const existingShare = await getDoc(sharedProjectRef);
    if (existingShare.exists()) {
      throw new Error('Project is already shared with this user');
    }

    // Load equipment and conductors for the shared project
    const equipmentCollection = getEquipmentCollection(projectId);
    const equipmentSnapshot = await getDocs(equipmentCollection);
    const equipment = equipmentSnapshot.docs
      .map(doc => doc.data())
      .sort((a, b) => a.index - b.index);

    const conductorsCollection = getConductorsCollection(projectId);
    const conductorsSnapshot = await getDocs(conductorsCollection);
    const conductors = conductorsSnapshot.docs
      .map(doc => doc.data())
      .sort((a, b) => a.index - b.index);

    // Create shared project entry with full project data (matching the working structure)
    const sharedProjectData = {
      id: projectId.toString(),
      originalProjectId: projectId.toString(),
      originalUserId: userId,
      sharedWith: recipientUserId,
      sharedBy: userId,
      sharedAt: new Date().toISOString(),
      name: projectData.name,
      customer: projectData.customer,
      customerAddress: projectData.customerAddress || '',
      customerPhone: projectData.customerPhone || '',
      type: projectData.type || 'residential',
      status: projectData.status || 'new',
      mapCenter: projectData.mapCenter || [-122.4194, 37.7749],
      mapZoom: projectData.mapZoom || 13,
      mapStyle: projectData.mapStyle || 'streets',
      equipment: equipment,
      conductors: conductors,
      equipmentCount: equipment.length,
      conductorsCount: conductors.length,
      isShared: true,
      isReadOnly: true,
      createdAt: projectData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    // Validate document size before creating
    try {
      const documentSize = validateDocumentSize(sharedProjectData);
    } catch (sizeError) {
      // Try to create without equipment and conductors if too large
      const minimalSharedProjectData = {
        id: projectId.toString(),
        originalProjectId: projectId.toString(),
        originalUserId: userId,
        sharedWith: recipientUserId,
        sharedBy: userId,
        sharedAt: new Date().toISOString(),
        name: projectData.name,
        customer: projectData.customer,
        customerAddress: projectData.customerAddress || '',
        customerPhone: projectData.customerPhone || '',
        type: projectData.type || 'residential',
        status: projectData.status || 'new',
        mapCenter: projectData.mapCenter || [-122.4194, 37.7749],
        mapZoom: projectData.mapZoom || 13,
        mapStyle: projectData.mapStyle || 'streets',
        equipment: [],
        conductors: [],
        equipmentCount: 0,
        conductorsCount: 0,
        isShared: true,
        isReadOnly: true,
        createdAt: projectData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(sharedProjectRef, minimalSharedProjectData);
      return {
        success: true,
        message: `Project shared successfully with ${email} (minimal data due to size)`
      };
    }

    await setDoc(sharedProjectRef, sharedProjectData);

    return {
      success: true,
      message: `Project shared successfully with ${email}`
    };

  } catch (error) {
    console.error('Error sharing project:', error);
    throw error;
  }
};

// Get projects shared with current user
export const getSharedProjects = async () => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Query shared projects where current user is the recipient
    const sharedProjectsQuery = query(
      getSharedProjectsCollection(),
      where('sharedWith', '==', userId)
    );

    const sharedProjectsSnapshot = await getDocs(sharedProjectsQuery);
    
    const sharedProjects = [];

    for (const docSnapshot of sharedProjectsSnapshot.docs) {
      const data = docSnapshot.data();
      
      // Get the sharer's email address
      let sharerEmail = 'Unknown';
      try {
        const sharerUserDoc = await getDoc(doc(db, 'users', data.sharedBy));
        if (sharerUserDoc.exists()) {
          const sharerUserData = sharerUserDoc.data();
          sharerEmail = sharerUserData.email;
        }
      } catch (error) {
        console.error('Error getting sharer user data:', error);
      }
      
      // Properly reconstruct the project data with correct array structures
      const fullProject = {
        ...data,
        // Ensure arrays are properly reconstructed
        equipment: Array.isArray(data.equipment) ? data.equipment : [],
        conductors: Array.isArray(data.conductors) ? data.conductors : [],
        // Ensure coordinates are properly formatted
        mapCenter: Array.isArray(data.mapCenter) ? data.mapCenter : [-122.4194, 37.7749],
        // Ensure counts are numbers
        equipmentCount: typeof data.equipmentCount === 'number' ? data.equipmentCount : (Array.isArray(data.equipment) ? data.equipment.length : 0),
        conductorsCount: typeof data.conductorsCount === 'number' ? data.conductorsCount : (Array.isArray(data.conductors) ? data.conductors.length : 0),
        // Add shared project flags
        isShared: true,
        isReadOnly: true,
        sharedAt: data.sharedAt,
        sharedBy: data.sharedBy,
        sharerEmail: sharerEmail,
        // Ensure other required fields
        type: data.type || 'residential',
        status: data.status || 'new',
        mapZoom: typeof data.mapZoom === 'number' ? data.mapZoom : 13,
        mapStyle: data.mapStyle || 'streets',
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: data.updatedAt || new Date().toISOString()
      };

      sharedProjects.push(fullProject);
    }

    return sharedProjects;

  } catch (error) {
    console.error('Error getting shared projects:', error);
    throw error;
  }
};

// Get list of users who have access to a project
export const getProjectAccessList = async (projectId) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Check if user owns the project
    const projectDoc = await getDoc(getProjectDoc(projectId));
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const projectData = projectDoc.data();
    if (projectData.userId !== userId) {
      throw new Error('You can only view access for projects you own');
    }

    // Query shared projects for this project
    const sharedProjectsQuery = query(
      getSharedProjectsCollection(),
      where('originalProjectId', '==', projectId.toString())
    );

    const sharedProjectsSnapshot = await getDocs(sharedProjectsQuery);
    const accessList = [];

    for (const docSnapshot of sharedProjectsSnapshot.docs) {
      const data = docSnapshot.data();
      
      // Get the recipient user's email
      try {
        const recipientUserDoc = await getDoc(doc(db, 'users', data.sharedWith));
        if (recipientUserDoc.exists()) {
          const recipientUserData = recipientUserDoc.data();
          accessList.push({
            email: recipientUserData.email,
            sharedAt: data.sharedAt,
            sharedProjectId: data.id
          });
        }
      } catch (error) {
        console.error('Error getting recipient user data:', error);
      }
    }

    console.log('Project access list:', accessList.length, 'users');
    return accessList;

  } catch (error) {
    console.error('Error getting project access list:', error);
    throw error;
  }
};

// Revoke access for a specific user
export const revokeProjectAccess = async (projectId, email) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Check if user owns the project
    const projectDoc = await getDoc(getProjectDoc(projectId));
    if (!projectDoc.exists()) {
      throw new Error('Project not found');
    }

    const projectData = projectDoc.data();
    if (projectData.userId !== userId) {
      throw new Error('You can only revoke access for projects you own');
    }

    // Get the recipient user ID by email
    const usersRef = collection(db, 'users');
    const userQuery = query(usersRef, where('email', '==', email));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      throw new Error('User with this email address not found');
    }
    
    const recipientUser = userSnapshot.docs[0];
    const recipientUserId = recipientUser.id;

    // Find the shared project entry
    const sharedProjectId = `${projectId}_${recipientUserId}`;
    const sharedProjectRef = doc(getSharedProjectsCollection(), sharedProjectId);

    const sharedProjectDoc = await getDoc(sharedProjectRef);
    if (!sharedProjectDoc.exists()) {
      throw new Error('No access found for this email');
    }

    // Delete the shared project entry
    await deleteDoc(sharedProjectRef);

    console.log('Access revoked successfully:', {
      projectId,
      email,
      recipientUserId,
      sharedProjectId
    });

    return {
      success: true,
      message: `Access revoked for ${email}`
    };

  } catch (error) {
    console.error('Error revoking project access:', error);
    throw error;
  }
};

// Load shared project from shared_projects collection
export const loadSharedProject = async (projectId) => {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    // Query the shared project document
    const sharedProjectsQuery = query(
      getSharedProjectsCollection(),
      where('id', '==', projectId.toString()),
      where('sharedWith', '==', userId)
    );

    const sharedProjectsSnapshot = await getDocs(sharedProjectsQuery);
    
    if (sharedProjectsSnapshot.empty) {
      throw new Error('Shared project not found or access denied');
    }

    const sharedProjectDoc = sharedProjectsSnapshot.docs[0];
    const data = sharedProjectDoc.data();

    // Get the sharer's email address
    let sharerEmail = 'Unknown';
    try {
      const sharerUserDoc = await getDoc(doc(db, 'users', data.sharedBy));
      if (sharerUserDoc.exists()) {
        const sharerUserData = sharerUserDoc.data();
        sharerEmail = sharerUserData.email;
      }
    } catch (error) {
      console.error('Error getting sharer user data:', error);
    }

    // Validate coordinates function
    const validateCoordinates = (coords) => {
      if (!Array.isArray(coords)) return null;
      if (coords.length !== 2) return null;
      const [lng, lat] = coords;
      if (typeof lng !== 'number' || typeof lat !== 'number') return null;
      if (isNaN(lng) || isNaN(lat)) return null;
      return [lng, lat];
    };

    // Properly reconstruct equipment data
    const equipment = (data.equipment || []).map((item, index) => {
      // Validate coordinates
      const validLngLat = validateCoordinates(item.lngLat);
      const validCoordinates = validateCoordinates(item.coordinates);
      
      if (!validLngLat) {
        console.warn('Invalid equipment lngLat in shared project:', item.lngLat, 'for equipment:', item.type);
      }
      
      // Return equipment in exact format expected by GridMapProApp
      return {
        id: item.id || index + 1,
        type: item.type,
        lngLat: validLngLat || [0, 0], // Fallback to prevent NaN
        coordinates: validCoordinates || validLngLat || [0, 0], // Keep both for compatibility
        details: item.details || {},
        notes: item.notes || '',
        photos: item.photos || [],
        properties: item.properties || {
          ...item.details,
          notes: item.notes,
          photos: item.photos || []
        }
      };
    });

    // Properly reconstruct conductors data
    const conductors = (data.conductors || []).map((item, index) => {
      console.log('Processing conductor item:', item);
      console.log('Conductor coordinates type:', typeof item.coordinates);
      console.log('Conductor coordinates:', item.coordinates);
      
      // Reconstruct coordinates from flattened format
      let reconstructedCoordinates = [];
      if (item.coordinates && Array.isArray(item.coordinates)) {
        console.log('Coordinates is array, length:', item.coordinates.length);
        // Check if coordinates are in flattened format (objects with lng/lat) or array format
        if (item.coordinates.length > 0 && typeof item.coordinates[0] === 'object' && 'lng' in item.coordinates[0]) {
          // Flattened format: convert back to array format
          console.log('Converting from flattened format');
          reconstructedCoordinates = item.coordinates.map(coord => [coord.lng, coord.lat]);
        } else {
          // Already in array format (for backward compatibility)
          console.log('Using array format directly');
          reconstructedCoordinates = item.coordinates;
        }
      } else if (item.coordinates && typeof item.coordinates === 'string') {
        // Handle case where coordinates might be serialized as string
        console.log('Coordinates is string, trying to parse');
        try {
          const parsed = JSON.parse(item.coordinates);
          if (Array.isArray(parsed)) {
            reconstructedCoordinates = parsed;
          }
        } catch (parseError) {
          console.warn('Failed to parse coordinates string:', parseError);
        }
      }
      
      console.log('Reconstructed coordinates:', reconstructedCoordinates);
      
      // Validate reconstructed coordinates
      const validCoordinates = reconstructedCoordinates
        .map(coord => validateCoordinates(coord))
        .filter(Boolean);
      
      console.log('Valid coordinates after validation:', validCoordinates);
      
      if (!validCoordinates || validCoordinates.length < 2) {
        console.warn('Invalid conductor coordinates in shared project:', item.coordinates, 'for conductor:', item.type);
      }
      
      // Return conductors in exact format expected by GridMapProApp
      return {
        id: item.id || index + 1,
        type: item.type,
        coordinates: validCoordinates || [[0, 0], [0, 0]], // Keep as array format
        wireSize: item.wireSize || '',
        length: item.length || 0,
        notes: item.notes || '',
        photos: item.photos || [],
        properties: item.properties || {
          type: item.type,
          wireSize: item.wireSize,
          length: item.length,
          notes: item.notes,
          photos: item.photos || []
        }
      };
    });

    // Validate map center
    const validMapCenter = validateCoordinates(data.mapCenter);
    if (!validMapCenter) {
      console.warn('Invalid map center in shared project:', data.mapCenter);
    }

    // Combine all data - match exact structure from GridMapProApp
    const fullProject = {
      id: data.id,
      name: data.name,
      customer: data.customer,
      customerAddress: data.customerAddress,
      customerPhone: data.customerPhone,
      type: data.type,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      // Map data
      mapCenter: validMapCenter || [-122.4194, 37.7749], // Fallback to prevent NaN
      mapZoom: data.mapZoom || 13,
      mapStyle: data.mapStyle || 'streets',
      // Viewport data if available - reconstruct bounds from flattened format
      viewport: data.viewport ? {
        bounds: data.viewport.bounds ? [
          data.viewport.bounds.southwest ? [data.viewport.bounds.southwest.lng, data.viewport.bounds.southwest.lat] : null,
          data.viewport.bounds.northeast ? [data.viewport.bounds.northeast.lng, data.viewport.bounds.northeast.lat] : null
        ].filter(Boolean) : null,
        padding: data.viewport.padding || null
      } : null,
      // Equipment and conductors
      equipment: equipment,
      conductors: conductors,
      // Shared project flags
      isShared: true,
      isReadOnly: true,
      sharedAt: data.sharedAt,
      sharedBy: data.sharedBy,
      sharerEmail: sharerEmail
    };

    console.log('Shared project loaded:', data.id, {
      equipment: equipment.length,
      conductors: conductors.length,
      mapCenter: fullProject.mapCenter,
      mapZoom: fullProject.mapZoom,
      isShared: true
    });
    
    return fullProject;
    
  } catch (error) {
    console.error('Error loading shared project:', error);
    throw error;
  }
}; 